import { RequestPaymentResult } from 'features/payment/types';

import { useFirstDataCardPayment } from '../../../payment/hooks/use-first-data-card-payment';
import { useRequestApplePayPayment } from '../../../payment/hooks/use-request-apple-pay-payment';
import { useRequestBraintreePayment } from '../../../payment/hooks/use-request-braintree-payment';
import { useRequestCashAppPayment } from '../../../payment/hooks/use-request-cash-app-payment';
import { useRequestGooglePayPayment } from '../../../payment/hooks/use-request-google-pay-payment';
import { useCheckoutContext } from '../use-checkout-context';

/**
 * Aggregates payment details from various payment providers based on the current checkout state.
 *
 * Each provider's `requestPayment()` returns a `Promise<IPaymentDetails | undefined>`:
 * - Resolves with payment details on success
 * - Resolves with `undefined` or rejects (in one case) if the user cancels/dismisses
 * - Rejects with `PaymentRequestError` on failure
 *
 * This enables an imperative flow in the checkout submit: `const details = await requestPayment()`.
 */
export const useRequestPaymentDetails: () => RequestPaymentResult = () => {
  const {
    billingCountry,
    showReloadPaymentMethods,
    paymentValues,
    reloadPaymentValues,
    isPaymentProcessor,
    isPaymentType,
    totals,
    serverOrder,
    cartEntries,
  } = useCheckoutContext();
  const cardPayment = useFirstDataCardPayment({
    firsDataCard: {
      paymentValues: showReloadPaymentMethods ? reloadPaymentValues : paymentValues,
    },
  });
  const cashAppPayment = useRequestCashAppPayment({
    cashapp: {
      total: totals?.grandTotal || 0,
      tax: totals?.taxCents ?? 0,
      tip: totals?.tipAmount ?? 0,
    },
  });
  const braintreePayment = useRequestBraintreePayment({});
  const googlePayment = useRequestGooglePayPayment({
    googlePay: { billingCountry, total: totals?.grandTotal ?? 0, reloadAmount: 0 },
  });
  const applePayment = useRequestApplePayPayment({
    applePay: {
      serverOrder,
      cartEntries,
      tip: totals?.tipAmount ?? 0,
      reloadAmount: 0,
      billingCountry,
    },
  });

  if (isPaymentProcessor.braintree) {
    return braintreePayment;
  }

  if (isPaymentType.cashApp) {
    return cashAppPayment;
  }

  if (isPaymentType.googlePay) {
    return googlePayment;
  }

  if (isPaymentType.applePay) {
    return applePayment;
  }

  return cardPayment;
};
